import { useEffect, useRef, useState } from 'react'
// import { posthog } from 'posthog-js'
import { convertKeysToSnakeCase, getPageName } from '../../utils'
import { TOptionTracking, TTracking, TTrackingQueue } from '../../types'

const INTERVAL_TIME = 500

// This useHog function is used to centralize PostHog.
// In the future, any further requirements or changes can
// easily be updated here.
/**
 * A Hog Hook that return a tracking function and a tracking queue function.
 *
 * @returns  tracking, trackingQueue
 */
const useHog = () => {
    // Queue
    const [isEnableQueue, setIsEnableQueue] = useState<boolean>(false)
    const [trackingQueues, setTrackingQueues] = useState<TTrackingQueue[]>([])
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null)

    const tracking = (params: TTracking, options?: Partial<TOptionTracking>) => {
        try {
            if (!window.posthog) {
                console.error('PostHog is not ready!')
                return
            }
            const { eventName = '', properties = {} } = params
            // Do not tracking when there is no eventName provided
            if (eventName === '') {
                console.error('eventName is empty!')
                return
            }
            // Parse page-name from window.location.pathname
            properties.pageName = getPageName()

            const parsedProperties = convertKeysToSnakeCase(properties)
            window.posthog.capture(eventName, parsedProperties, options)
        } catch (ex) {
            console.error('PostHog Error', ex)
        }
    }

    const trackingQueue = (params: TTracking, options?: Partial<TOptionTracking>) => {
        try {
            // Run tracking when postHog is ready! No need to queue
            if (window.posthog) {
                tracking(params, options)
                return
            }
            // PostHog is not ready then we should add it to queue
            const newTrackingQueues = [...trackingQueues]
            newTrackingQueues.push({
                id: `${params.eventName}_${trackingQueues.length + 1}`,
                trackingParams: params,
                options
            })
            setTrackingQueues(newTrackingQueues)
            setIsEnableQueue(true)
        } catch (ex) {
            console.error('Tracking Queue Error', ex)
        }
    }

    const checkingPostHog = () => {
        if (!window.posthog) {
            return
        }
        // Disable interval to prevent javascript running endless
        if (trackingQueues.length === 0) {
            setIsEnableQueue(false)
            return
        }
        // Loop through queues
        trackingQueues.forEach((trackingItem) => {
            // Send tracking event
            tracking(trackingItem.trackingParams, trackingItem?.options)
        })
        // All tracking is sent then we should empty it
        setTrackingQueues([])
        setIsEnableQueue(false)
    }

    useEffect(() => {
        if (!isEnableQueue) {
            return
        }
        if (trackingQueues.length === 0) {
            return
        }
        intervalIdRef.current = setInterval(checkingPostHog, INTERVAL_TIME)

        return () => {
            intervalIdRef.current && clearInterval(intervalIdRef.current)
            intervalIdRef.current = null
        }
    }, [isEnableQueue, trackingQueues])

    return {
        tracking,
        trackingQueue
    }
}

export default useHog
